import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import * as api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { qk, useDbSync } from "../../lib/queries";
import { OfflineBanner, PageSkeleton } from "../common/layout-bits";
import { OverlayHost } from "../schedule/OverlayHost";
import { BottomNav, MobileHeader, ScheduleFab, TopBar } from "./Bars";
import { PageMetaProvider } from "./page-meta";
import { Rail, Sidebar } from "./Sidebar";

/** Creates due H-7 / H-1 / D-day reminders on open and every hour (a backend cron later). */
function useReminderSync(userId: string | null) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    const run = () =>
      api.syncReminders(userId).then((n) => {
        if (alive && n > 0) void qc.invalidateQueries({ queryKey: qk.notifications(userId) });
      });
    void run();
    const timer = window.setInterval(run, 60 * 60 * 1000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [userId, qc]);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

/** One app shell for every page: sidebar ≥1024, icon rail 768–1023, header + bottom nav below 768. */
export function AppLayout() {
  const { userId } = useAuth();
  useDbSync();
  useReminderSync(userId);
  return (
    <PageMetaProvider>
      <ScrollToTop />
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-lg focus:bg-primary-container focus:px-3 focus:py-2 focus:text-on-primary">
        Skip to content
      </a>
      <Sidebar />
      <Rail />
      <div className="min-h-dvh md:pl-[72px] lg:pl-60">
        <TopBar />
        <MobileHeader />
        <OfflineBanner />
        <main id="main" className="relative">
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <BottomNav />
      <ScheduleFab />
      <OverlayHost />
    </PageMetaProvider>
  );
}
